import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'Utils/constants';
import { useUser } from 'Components/Organisms/UserProvider/useUser';
import Icon from 'Components/Atoms/Icon/Icon';
import {
    FloatingFocusManager,
    autoUpdate,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import Avatar from 'Components/Atoms/Avatar/Avatar';
import { AnimatePresence, motion } from 'framer-motion';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import Skeleton from 'Components/Atoms/Skeleton/Skeleton';
import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import ChangeWorkspaceButton from 'Components/Organisms/ChangeWorkspaceButton/ChangeWorkspaceButton';
import useProjectSelected from 'Hooks/useProjectSelected';

export default function AppLayoutDesktopUserMenu() {
    const { user, logout } = useUser();
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const { t } = useTranslation('common');
    const { workspace, isLoading } = useWorkspaceSelected();
    const { project } = useProjectSelected();
    const workspaceName = workspace?.name || '';
    const projectName = project?.name || '';

    const handleToggle = () => {
        setExpanded((prevExpanded) => !prevExpanded);
    };

    const { x, y, refs, strategy, context } = useFloating({
        whileElementsMounted: autoUpdate,
        open: expanded,
        placement: 'bottom-end',
        onOpenChange: setExpanded,
        strategy: 'absolute',
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'menu' }),
        useDismiss(context),
    ]);

    const menuItems = [
        {
            icon: 'user',
            children: t('edit-profile'),
            testId: 'header.edit-profile',
            onClick: () => {
                navigate(ROUTES.profile);
                setExpanded(false);
            },
        },
        ...(user?.is_partner || user?.is_support
            ? [
                  {
                      icon: 'coins',
                      children: t('partners'),
                      testId: 'header.partners',
                      onClick: () => {
                          navigate(ROUTES.partners);
                          setExpanded(false);
                      },
                  },
              ]
            : []),
        {
            icon: 'exit',
            children: t('logout'),
            testId: 'header.logout',
            onClick: () => logout(),
        },
    ];

    return (
        <div>
            <button
                onClick={handleToggle}
                ref={refs.setReference}
                type="button"
                data-testid="header.user-menu"
                {...getReferenceProps()}
                className="flex items-center gap-3 text-left text-dark-blue px-2 py-3"
            >
                <Avatar name={projectName} className="shrink-0" />

                <div className="text-xs grow max-w-[200px] overflow-hidden">
                    <div className="font-medium truncate">{projectName}</div>
                    {isLoading ? (
                        <Skeleton variant="text" width={90} />
                    ) : (
                        <div className="text-gray-500 truncate">{workspaceName}</div>
                    )}
                </div>

                <OutlineChevronDown size={20} color="currentColor" className="shrink-0 text-dark-blue" />
            </button>

            <AnimatePresence>
                {expanded && (
                    <FloatingFocusManager context={context}>
                        <motion.div
                            initial={{ opacity: 0, y: -12 }}
                            animate={{ opacity: 1, y: -4 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                            className="rounded-lg bg-white shadow-sm border border-gray-200 overflow-hidden w-[280px] z-dropdown"
                            {...getFloatingProps({
                                ref: refs.setFloating,
                                style: {
                                    position: strategy,
                                    top: y ?? 0,
                                    left: x ?? 0,
                                },
                            })}
                        >
                            <div className="flex flex-col">
                                <ChangeWorkspaceButton />

                                <div className="h-[1px] bg-gray-200" />
                                <div className="p-2">
                                    {menuItems.map((item, index) => (
                                        <button
                                            key={index}
                                            className="flex gap-2 items-center px-2 py-2 text-sm text-gray-500 hover:text-dark-blue transition-colors hover:bg-gray-100 w-full text-left rounded-sm"
                                            type="button"
                                            data-testid={item.testId}
                                            onClick={item.onClick}
                                        >
                                            <Icon name={item.icon} size={18} color="currentColor" />
                                            <span>{item.children}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </FloatingFocusManager>
                )}
            </AnimatePresence>
        </div>
    );
}
